import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { motion } from "framer-motion";
import { EVENT, YOUTUBE_ID, YOUTUBE_START, HERO_POSTER } from "../data/eventData";

// Vídeo de fundo via YouTube IFrame API · se o player falhar, esconde e mantém o poster
function useHeroVideo() {
  const [videoOk, setVideoOk] = useState(false);

  useEffect(() => {
    let player;
    let cancelled = false;

    const mount = () => {
      if (cancelled) return;
      player = new window.YT.Player("hero-yt", {
        videoId: YOUTUBE_ID,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          loop: 1,
          playlist: YOUTUBE_ID,
          start: YOUTUBE_START,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          iv_load_policy: 3,
          disablekb: 1,
          fs: 0,
        },
        events: {
          onReady: (e) => {
            e.target.mute();
            e.target.playVideo();
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) setVideoOk(true);
          },
          onError: () => setVideoOk(false),
        },
      });
    };

    if (window.YT && window.YT.Player) {
      mount();
    } else {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (prev) prev();
        mount();
      };
    }

    return () => {
      cancelled = true;
      try { player && player.destroy && player.destroy(); } catch (_) {}
    };
  }, []);

  return videoOk;
}

export default function Hero({ onCTA }) {
  const videoOk = useHeroVideo();

  return (
    <section id="hero" className="relative min-h-[100svh] overflow-hidden flex flex-col bg-black" data-testid="hero">
      {/* Poster · base sempre presente */}
      <img
        src={HERO_POSTER}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-60"
      />

      {/* Vídeo de fundo · começa em 0:52, mudo, em loop · só aparece quando está tocando */}
      <div
        className={`absolute inset-0 overflow-hidden pointer-events-none transition-opacity duration-1000 ${
          videoOk ? "opacity-100" : "opacity-0"
        }`}
        aria-hidden="true"
      >
        <div id="hero-yt" />
      </div>

      {/* Overlay cinematográfico preto · equilíbrio entre vídeo visível e leitura */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-black/85" />
      <div className="absolute inset-0 bg-[radial-gradient(900px_620px_at_50%_46%,rgba(0,0,0,0.5),transparent_72%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(1100px_560px_at_50%_108%,rgba(0,142,255,0.18),transparent_65%)]" />

      {/* Conteúdo centralizado · mesma composição da referência */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-5 pt-14 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-9 sm:mb-11"
        >
          <img
            src="/logo-original.png"
            alt="Pregoeiros Summit"
            className="h-14 sm:h-20 w-auto mx-auto"
            data-testid="hero-logo"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="inline-flex items-center gap-2 sm:gap-2.5 rounded-full border border-white/15 bg-white/[0.06] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.10)] px-3.5 sm:px-5 py-2.5 mb-8 sm:mb-10"
          data-testid="hero-date-pill"
        >
          <CalendarDays size={16} className="text-brand-cyan shrink-0" />
          <span className="text-xs sm:text-base text-white/90 whitespace-nowrap">
            <strong className="font-bold text-brand-cyan">03, 04 e 05 de Março</strong>
            <span className="text-white/50 mx-1.5">|</span>
            Florianópolis · SC
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-extrabold uppercase text-white text-3xl sm:text-5xl lg:text-[56px] leading-[1.12] tracking-[-0.01em] max-w-6xl [text-shadow:0_4px_28px_rgba(2,6,18,0.65)]"
          data-testid="hero-headline"
        >
          <span className="lg:whitespace-nowrap">
            A <span className="text-gradient-blue">maior experiência</span> do Brasil
          </span>
          <br className="hidden lg:block" />{" "}
          <span className="lg:whitespace-nowrap">em licitações e contratos</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.38, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 sm:mt-7 max-w-2xl text-white/85 text-sm sm:text-lg leading-relaxed [text-shadow:0_2px_16px_rgba(2,6,18,0.6)]"
          data-testid="hero-subheadline"
        >
          Três dias de imersão em uma experiência que{" "}
          <strong className="text-white font-bold">redefine a sua atuação em licitações.</strong>
        </motion.p>

        {/* CTAs · pills com anel externo, sem ícones */}
        <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5">
          <motion.button
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.52, ease: [0.22, 1, 0.36, 1] }}
            onClick={onCTA}
            className="group relative rounded-full p-[5px] border border-white/25 transition-all duration-300 hover:border-white/50 hover:shadow-[0_18px_50px_rgba(3,10,26,0.60)]"
            data-testid="hero-cta"
          >
            <span className="flex items-center justify-center rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan px-9 sm:px-12 py-4 transition-transform duration-300 group-hover:scale-[1.015]">
              <span className="font-display font-semibold uppercase tracking-[0.04em] text-white text-sm sm:text-base">
                Quero garantir meu lugar
              </span>
            </span>
          </motion.button>

          <motion.a
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.64, ease: [0.22, 1, 0.36, 1] }}
            href="/programacao-ps27.pdf"
            download="Folder_PS27.pdf"
            className="group relative rounded-full p-[5px] border border-white/15 transition-all duration-300 hover:border-white/40 hover:shadow-[0_16px_44px_rgba(3,10,26,0.55)]"
            data-testid="hero-download-btn"
          >
            <span className="flex items-center justify-center rounded-full bg-white/[0.06] backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.10)] px-9 sm:px-12 py-4 transition-transform duration-300 group-hover:scale-[1.015] group-hover:bg-white/[0.10]">
              <span className="font-display font-semibold uppercase tracking-[0.04em] text-white text-sm sm:text-base">
                Baixar programação
              </span>
            </span>
          </motion.a>
        </div>
      </div>

      <span className="sr-only">{EVENT.dateLabel} · Florianópolis · Santa Catarina</span>
    </section>
  );
}
